import { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@radix-ui/react-collapsible';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { cn } from 'lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useTheme } from 'components/core/theme-provider';

interface ExpandableThinkContainerProps {
  content: string;
  className?: string;
  isThinking?: boolean;
}

export default function ExpandableThinkContainer({
  content,
  className,
  isThinking,
}: ExpandableThinkContainerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const { theme } = useTheme();
  return (
    <Collapsible open={open} onOpenChange={setOpen} className={cn(className)}>
      <CollapsibleTrigger className={`flex justify-start items-center gap-2 rounded-lg ${theme === 'dark' ? 'bg-[#27272c]' : 'bg-[#e9e9e980]'} px-2 py-1`}>
        <span className="italic text-sm">
          {isThinking ? `${t('THINKING')}...` : `${t('THOUGHTS')}`}
        </span>
        {open ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
      </CollapsibleTrigger>
      <CollapsibleContent className={`mt-1 ${theme === 'dark' ? 'bg-[#27272c]' : 'bg-[#e9e9e980]'} p-2 rounded-lg transition-all data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down`}>
        <div className="italic text-xs text-inherit">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
              ul: ({ children }) => <ul className="list-disc pl-4 mb-1">{children}</ul>,
              ol: ({ children, start }) => <ol start={start} className="list-decimal pl-4 mb-1">{children}</ol>,
              li: ({ children }) => <li className="mb-0.5">{children}</li>,
              code: ({ inline, className, children, ...props }: any) => {
                const match = /language-(\w+)/.exec(className || '');
                if (!inline && match) {
                  return (
                    <SyntaxHighlighter
                      style={dracula}
                      language={match[1]}
                      PreTag="pre"
                      customStyle={{
                        backgroundColor: '#282C34',
                        padding: '1rem',
                        borderRadius: '0.375rem',
                        overflowX: 'auto',
                        fontSize: '0.875rem',
                      }}
                      {...props}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  );
                }
                return (
                  <code
                    className="rounded-[7px] border border-gray-300/60 bg-[#f0f1f3] px-1.5 py-0.5 font-mono text-sm text-[#ab5152] dark:border-gray-600 dark:bg-muted dark:text-[#fca5a5]"
                    {...props}
                  >
                    {children}
                  </code>
                );
              },
              pre: ({ children }) => (
                <pre className="w-full bg-gray-800 text-gray-100 rounded p-2 overflow-x-auto mb-1">
                  {children}
                </pre>
              ),
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-gray-600 pl-4 italic mb-1">
                  {children}
                </blockquote>
              ),
            }}
          >
            {content.trim()}
          </ReactMarkdown>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
